import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { ProgressBar } from "@/components/ProgressBar";
import { StatusChip } from "@/components/StatusChip";
import { ArrowRight, PlayCircle, Lock, CheckCircle2, ClipboardList } from "lucide-react";

export default function NurseHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/nurse/dashboard");
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const requestAccess = async () => {
    setRequesting(true);
    try {
      await api.post("/nurse/request-access");
      toast.success("Access requested. MITRA will review shortly.");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRequesting(false); }
  };

  if (!data) return <Loader />;

  return (
    <div className="space-y-5" data-testid="nurse-home">
      <div>
        <p className="text-sm text-[#434654]">Welcome back,</p>
        <h1 className="text-[24px] font-bold text-[#1c1b1b] leading-tight">{data.name}</h1>
      </div>

      {data.access_status !== "approved" && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="access-card">
          {data.access_status === "none" && (
            <>
              <h2 className="font-semibold text-[#1c1b1b] mb-1">Request Training Access</h2>
              <p className="text-sm text-[#434654] mb-4">
                Complete your profile, then request access to begin your MITRA training modules.
              </p>
              {!data.profile_complete && (
                <button
                  data-testid="complete-profile-cta"
                  onClick={() => navigate("/app/profile")}
                  className="w-full h-11 mb-3 rounded-lg border border-[#E5E7EB] font-semibold text-[#003d9b] hover:bg-gray-50 transition-colors"
                >
                  Complete Profile First
                </button>
              )}
              <button
                data-testid="request-access-btn"
                disabled={!data.profile_complete || requesting}
                onClick={requestAccess}
                className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors disabled:opacity-50"
              >
                Request Access
              </button>
            </>
          )}
          {data.access_status === "pending" && (
            <div className="flex items-center gap-3" data-testid="access-pending">
              <StatusChip status="pending" />
              <p className="text-sm text-[#434654]">Your request is awaiting MITRA approval.</p>
            </div>
          )}
          {data.access_status === "rejected" && (
            <div className="flex items-center gap-3" data-testid="access-rejected">
              <StatusChip status="rejected" />
              <p className="text-sm text-[#434654]">Access not approved. Please contact MITRA.</p>
            </div>
          )}
        </div>
      )}

      {data.total_modules > 0 && (
        <>
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="overall-progress-card">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-semibold text-[#1c1b1b]">Overall Progress</h2>
              <span className="text-[28px] font-bold text-[#003d9b]" data-testid="overall-progress-value">
                {data.overall_progress}%
              </span>
            </div>
            <ProgressBar value={data.overall_progress} testId="overall-progress-bar" />
            <p className="text-sm text-[#434654] mt-2">
              {data.completed_modules} of {data.total_modules} modules completed
            </p>
          </div>

          {data.all_complete ? (
            <div className="bg-[#004e33] rounded-lg p-6 text-white" data-testid="training-complete-banner">
              <CheckCircle2 className="w-8 h-8 mb-2" />
              <h2 className="text-xl font-bold">MITRA Training Completed</h2>
              <p className="text-sm text-white/80 mt-1">All assigned modules are done. MITRA will review your eligibility.</p>
            </div>
          ) : data.up_next ? (
            <div
              className="bg-[#003d9b] rounded-lg p-6 text-white cursor-pointer"
              data-testid="up-next-card"
              onClick={() => navigate(`/app/modules/${data.up_next.id}`)}
            >
              <p className="text-xs uppercase tracking-wider text-white/70 font-bold">Up Next</p>
              <h2 className="text-xl font-bold mt-1">{data.up_next.title}</h2>
              <p className="text-sm text-white/80 mt-1 line-clamp-2">{data.up_next.description}</p>
              <div className="mt-4 inline-flex items-center gap-2 bg-white text-[#003d9b] font-semibold rounded-lg px-4 h-11">
                Continue Training <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          ) : null}

          <div>
            <h2 className="font-semibold text-[#1c1b1b] mb-3">Your Assigned Training</h2>
            <div className="space-y-3">
              {data.modules.map((m) => (
                <button
                  key={m.id}
                  data-testid={`assigned-module-${m.id}`}
                  onClick={() => navigate(`/app/modules/${m.id}`)}
                  className="w-full text-left bg-white rounded-lg border border-[#E5E7EB] p-4 flex items-center gap-3 hover:border-[#003d9b] transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#003d9b]/10 flex items-center justify-center shrink-0">
                    {m.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-[#004e33]" />
                    ) : (
                      <ClipboardList className="w-5 h-5 text-[#003d9b]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1c1b1b] truncate">{m.title}</p>
                    <p className="text-xs text-[#434654]">{m.estimated_minutes} min</p>
                  </div>
                  <StatusChip status={m.status} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
